import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {DropdownAsync} from "components/Dropdown/DropdownAsync";
import {getCustomPaymentMethodOptions} from "services/invoiceService";
import {PAYMENT_METHOD_OPTIONS} from "utils/invoiceMapper";

export function PaymentMethodFields({formState, errors = {}, onFieldChange}) {
	const [defaultCustomPaymentOptions, setDefaultCustomPaymentOptions] = useState([]);
	const [isLoadingDefaultCustomPaymentOptions, setIsLoadingDefaultCustomPaymentOptions] = useState(false);
	const prevHospitalIdRef = useRef(formState?.hospitalId || "");

	const selectedCustomPaymentMethod = useMemo(() => {
		if (!formState?.customPaymentMethodId) {
			return null;
		}

		return {
			value: formState.customPaymentMethodId,
			label: formState?.customPaymentMethodName || `#${formState.customPaymentMethodId}`,
		};
	}, [formState?.customPaymentMethodId, formState?.customPaymentMethodName]);

	useEffect(() => {
		const previousHospitalId = prevHospitalIdRef.current;
		const currentHospitalId = formState?.hospitalId || "";

		if (
			previousHospitalId &&
			currentHospitalId &&
			previousHospitalId !== currentHospitalId &&
			formState?.paymentMethod === "custom" &&
			formState?.customPaymentMethodId
		) {
			onFieldChange("customPaymentMethodId", "");
			onFieldChange("customPaymentMethodName", "");
		}

		prevHospitalIdRef.current = currentHospitalId;
	}, [formState?.customPaymentMethodId, formState?.hospitalId, formState?.paymentMethod, onFieldChange]);

	useEffect(() => {
		let isMounted = true;

		async function loadDefaultOptions() {
			if (formState?.paymentMethod !== "custom" || !formState?.hospitalId) {
				if (isMounted) {
					setIsLoadingDefaultCustomPaymentOptions(false);
					setDefaultCustomPaymentOptions(selectedCustomPaymentMethod ? [selectedCustomPaymentMethod] : []);
				}

				return;
			}

			if (isMounted) {
				setIsLoadingDefaultCustomPaymentOptions(true);
			}

			try {
				const response = await getCustomPaymentMethodOptions({
					hospitalId: formState.hospitalId,
					limit: 10,
					page: 1,
				});

				if (!isMounted) {
					return;
				}

				const fetchedOptions = (response?.data?.rows || response?.data || [])
					.map(item => ({
						value: item?.id,
						label: item?.name || `#${item?.id}`,
					}))
					.filter(item => item.value);

				const uniqueOptions = selectedCustomPaymentMethod
					? [
							selectedCustomPaymentMethod,
							...fetchedOptions.filter(item => item.value !== selectedCustomPaymentMethod.value),
					  ]
					: fetchedOptions;

				setDefaultCustomPaymentOptions(uniqueOptions);
				setIsLoadingDefaultCustomPaymentOptions(false);
			} catch (error) {
				if (isMounted) {
					setDefaultCustomPaymentOptions(selectedCustomPaymentMethod ? [selectedCustomPaymentMethod] : []);
					setIsLoadingDefaultCustomPaymentOptions(false);
				}
			}
		}

		loadDefaultOptions();

		return () => {
			isMounted = false;
		};
	}, [formState?.hospitalId, formState?.paymentMethod, selectedCustomPaymentMethod]);

	const fetchCustomPaymentMethodOptions = useCallback(
		async (inputValue, callback) => {
			const hospitalId = formState?.hospitalId;

			if (!hospitalId) {
				callback([]);

				return;
			}

			try {
				const response = await getCustomPaymentMethodOptions({
					hospitalId,
					search: inputValue || undefined,
					limit: 10,
					page: 1,
				});
				const options = (response?.data?.rows || response?.data || [])
					.map(item => ({
						value: item?.id,
						label: item?.name || `#${item?.id}`,
					}))
					.filter(item => item.value);

				callback(options);
			} catch (error) {
				callback([]);
			}
		},
		[formState?.hospitalId],
	);

	return (
		<div className="box-white ws p24 mb-3">
			<div className="ttl-20 mb-3">Payment Method</div>

			<div className="row g-2">
				<div className="col-md-4">
					<label className="setting-label required">Payment Method</label>
					<select
						className="form-control"
						value={formState?.paymentMethod || ""}
						onChange={event => onFieldChange("paymentMethod", event.target.value)}>
						<option value="">Pilih payment method</option>
						{PAYMENT_METHOD_OPTIONS.map(option => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
					{errors?.paymentMethod ? <p className="form-error-item-message">{errors.paymentMethod}</p> : null}
				</div>

				{formState?.paymentMethod === "custom" ? (
					<div className="col-md-4">
						<label className="setting-label required">Custom Payment Method</label>
						<DropdownAsync
							isClearable
							defaultOptions={defaultCustomPaymentOptions}
							defaultValue={selectedCustomPaymentMethod}
							disabled={!formState?.hospitalId}
							fetchOptions={fetchCustomPaymentMethodOptions}
							isError={!!errors?.customPaymentMethodId}
							isLoading={isLoadingDefaultCustomPaymentOptions}
							label={
								formState?.hospitalId ? "Cari custom payment method..." : "Hospital ID tidak tersedia"
							}
							loadingMessage={() => "Memuat custom payment method..."}
							noOptionsMessage={() => "Tidak ada custom payment method untuk rumah sakit ini"}
							onChange={option => {
								onFieldChange("customPaymentMethodId", option?.value || "");
								onFieldChange("customPaymentMethodName", option?.label || "");
							}}
						/>
						{errors?.customPaymentMethodId ? (
							<p className="form-error-item-message">{errors.customPaymentMethodId}</p>
						) : null}
					</div>
				) : null}

				{formState?.paymentMethod === "multiple_payment" ? (
					<>
						<div className="col-md-4">
							<label className="setting-label required">Cash Amount (&gt;= 0)</label>
							<input
								className="form-control"
								min="0"
								type="number"
								value={formState?.cashAmount || ""}
								onChange={event => onFieldChange("cashAmount", event.target.value)}
							/>
							{errors?.cashAmount ? <p className="form-error-item-message">{errors.cashAmount}</p> : null}
						</div>
						<div className="col-md-4">
							<label className="setting-label required">Additional Payment Method</label>
							<select
								className="form-control"
								value={formState?.additionalPaymentMethod || "share_link"}
								onChange={event => onFieldChange("additionalPaymentMethod", event.target.value)}>
								<option value="share_link">share_link</option>
							</select>
							{errors?.additionalPaymentMethod ? (
								<p className="form-error-item-message">{errors.additionalPaymentMethod}</p>
							) : null}
						</div>
					</>
				) : null}
			</div>
		</div>
	);
}
